import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { usePageContent } from '../../hooks/usePageContent';
/** Inline reset for <button> cards — CSS Modules :global() is tree-shaken by Vite
 *  when no local classes are exported, so we apply the reset inline instead. */
const buttonResetStyle = {
  appearance: 'none',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};

const ProductCard = ({
  product,
  selected = false,
  onClick,
  href = null,
  unavailable = false,
  className = '',
  generatedImage = null,
}) => {
  const { find } = usePageContent('product-card');
  const c = find('productCard') || {};

  const getVariants = (p) => p.VariantsInProduct || p.VariantsInBrowseStyle || [];

  const getUniqueColourHexes = (p) => {
    const variants = getVariants(p);
    const hexes = variants
      .map((v) => {
        const paths = [
          v?.ColourInfo?.ColourSelectorHexOuter,
          v?.ColourSelectorHexOuter,
          v?.colorInfo?.ColourSelectorHexOuter,
          v?.Colour,
          v?.Color,
        ];
        return paths.find((c) => c != null);
      })
      .filter(Boolean)
      .map((c) => String(c).toLowerCase().replace('#', ''));
    return [...new Set(hexes)].slice(0, 6);
  };

  const priceDisplay = (price) => (!price ? '0.00' : Number(price).toFixed(2));

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) onClick(product, e);
  };

  const variants = getVariants(product);

  const getVariantCost = (v) => {
    const ci = v.CostInfoForCreate;
    if (!ci) return null;
    if (Array.isArray(ci)) {
      const costs = ci.map((e) => e?.Cost).filter((x) => x != null);
      return costs.length > 0 ? Math.min(...costs) : null;
    }
    return ci.Cost ?? null;
  };
  const variantCosts = variants.map(getVariantCost).filter((c) => c != null);
  const showFrom = product._showFrom === true
    || (variantCosts.length > 1 && new Set(variantCosts).size > 1);
  const minVariantCost = variantCosts.length > 0 ? Math.min(...variantCosts) : null;

  const uniqueColourHexes = getUniqueColourHexes(product);
  const totalColourCount = new Set(
    variants
      .map((v) => {
        const paths = [
          v?.ColourInfo?.ColourSelectorHexOuter,
          v?.ColourSelectorHexOuter,
          v?.Colour,
          v?.Color,
        ];
        return paths.find((c) => c != null);
      })
      .filter(Boolean)
      .map((c) => String(c).toLowerCase().replace('#', ''))
  ).size;

  const itemClasses = ['products__list-item', unavailable ? '' : 'dimmable', className]
    .filter(Boolean)
    .join(' ');
  const linkClasses = [
    unavailable ? 'products__list-card' : 'products__list-link',
    selected ? 'products__list-link--ticked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const productNameWithVariant = (product._variantTheme && product._variantColour && product._variantTheme !== product._variantColour)
    ? `${product._variantTheme}: ${product._variantColour}`
    : product._variantColour
    ? product._variantColour
    : product.ProductName;

  const categoryLabel =
    product.CategoryDesc?.CategoryName || product.Category || '';

  const CardContent = (
    <>
      <img
        className="products__list-image"
        src={generatedImage || product.SampleImageId || '/images/placeholder-plate.png'}
        alt={`${productNameWithVariant} plate style`}
        loading="lazy"
      />
      <dl className="products__list-details">
        <dt className="products__list-details-key visuallyhidden">{c.nameLabel || 'Name:'}</dt>
        <dd className="products__list-details-value products__list-details-value--name">
          {productNameWithVariant}
        </dd>

        {c.priceTextOverrides?.[product.ProductName] ? (
          <dd className="products__list-details-value products__list-details-value--price">
            {c.priceTextOverrides[product.ProductName]}
          </dd>
        ) : (
          <>
            <dt className="products__list-details-key visuallyhidden">{c.priceLabel || 'Price'}</dt>
            <dd className="products__list-details-value products__list-details-value--price">
              {showFrom && `${c.fromLabel || 'From'} `}${priceDisplay(c.priceValueOverrides?.[product.ProductName] ?? (showFrom && !product._showFrom && minVariantCost !== null ? minVariantCost : product.ListPrice))}
            </dd>
          </>
        )}

        {product._otherColours && product._otherColours.length > 0 && (
          <>
            <dt className="products__list-details-key">{c.otherColoursLabel || 'Other colours:'}</dt>
            <dd className="products__list-details-value products__list-details-value--colours">
              <ul className="products__list-colour-list" aria-hidden="true">
                {product._otherColours.map(({ hex, swatchColour, colour }, i) => (
                  <li
                    key={i}
                    className="products__list-colour-item"
                    style={hex ? { backgroundColor: `#${hex}` } : undefined}
                    data-colour={swatchColour || (colour?.split(/\s+/)[0] || '').toLowerCase()}
                    title={colour}
                  />
                ))}
              </ul>
            </dd>
          </>
        )}
      </dl>
      <span className="products__list-false-cta" aria-hidden="true">
        {c.selectStyleText || 'Select style'}
      </span>
    </>
  );

  return (
    <li className={itemClasses}>
      {unavailable ? (
        <button type="button" className={linkClasses} onClick={handleClick}>{CardContent}</button>
      ) : href ? (
        <Link className={linkClasses} to={href}>
          {CardContent}
        </Link>
      ) : (
        <button type="button" className={linkClasses} style={buttonResetStyle} onClick={handleClick}>
          {CardContent}
        </button>
      )}
    </li>
  );
};

ProductCard.propTypes = {
  product: PropTypes.shape({
    ProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    ProductName: PropTypes.string.isRequired,
    SampleImageId: PropTypes.string,
    ListPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    CategoryDesc: PropTypes.shape({ CategoryName: PropTypes.string }),
    Category: PropTypes.string,
    VariantsInProduct: PropTypes.array,
    VariantsInBrowseStyle: PropTypes.array,
    _variantColour: PropTypes.string,
    _categoryName: PropTypes.string,
    _otherColours: PropTypes.arrayOf(PropTypes.shape({ hex: PropTypes.string, colour: PropTypes.string })),
  }).isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  href: PropTypes.string,
  unavailable: PropTypes.bool,
  className: PropTypes.string,
  generatedImage: PropTypes.string,
};

export default ProductCard;
